import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { date, money } from "@/lib/format";
import type { Paged, Shipment } from "@/lib/types";
import { Card, PageHeader, Skeleton, Stat, Table } from "@/components/ui";
import { useAuth } from "@/state/auth";

/**
 * What the account has spent, built from shipments rather than a separate
 * ledger.
 *
 * Every shipment freezes its price at booking, so this is the invoice: a
 * second source of truth would only be able to disagree with it.
 */
export function Billing() {
  const { account } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["billing-shipments"],
    queryFn: () => api.get<Paged<Shipment>>("/v1/shipments?limit=50"),
  });

  const shipments = (data?.data ?? []).filter((s) => s.status !== "cancelled");
  const month = new Date().toISOString().slice(0, 7);
  const thisMonth = shipments.filter((s) => s.created_at.startsWith(month));
  const spend = thisMonth.reduce((sum, s) => sum + s.price_cents, 0);
  const vat = thisMonth.reduce((sum, s) => sum + s.vat_cents, 0);

  return (
    <>
      <PageHeader title="Billing" subtitle="Charges, wallet and recent shipments." />

      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <Stat label="Spend this month" value={money(spend)} loading={isLoading} hint="Including VAT" />
        <Stat label="VAT this month" value={money(vat)} loading={isLoading} />
        <Stat
          label={account?.billing_mode === "invoiced" ? "Account balance" : "Wallet balance"}
          value={money(account?.balance_cents)}
          hint={account?.billing_mode === "invoiced" ? "Invoiced monthly" : "Prepaid"}
        />
      </div>

      <h2 className="mb-3 text-lg font-bold tracking-tight">Charges</h2>
      {isLoading ? (
        <Card>
          <Skeleton className="h-48 w-full" />
        </Card>
      ) : shipments.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-500">Nothing has been charged to this account yet.</p>
        </Card>
      ) : (
        <Table head={["Date", "Waybill", "Route", "Service", "VAT", "Total"]}>
          {shipments.map((shipment) => (
            <tr key={shipment.id}>
              <td className="ws-td text-ink-500">{date(shipment.created_at)}</td>
              <td className="ws-td font-mono text-xs font-semibold">{shipment.waybill}</td>
              <td className="ws-td">
                {shipment.collection.city} to {shipment.delivery.city}
              </td>
              <td className="ws-td text-ink-600">{shipment.service_level}</td>
              <td className="ws-td text-ink-500">{money(shipment.vat_cents)}</td>
              <td className="ws-td font-semibold">{money(shipment.price_cents)}</td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
